import { useState, useEffect } from "react";
import api from "../../utils/api";
import { INP, LBL, S, THEME, TblRow, TblCell, StatusBadge, tblHead, useIsMobile } from "./AdminComponents";
import { useLanguage } from "../../context/LanguageContext";

const { G, G3, O, O2 } = THEME;

const CONTENT = {
  ne: {
    statTotal:         "कुल",
    statPending:       "विचाराधीन",
    statApproved:      "स्वीकृत",
    statRejected:      "अस्वीकृत",
    deltaSoFar:        "अहिलेसम्म",
    deltaReview:       "समीक्षा",
    deltaVisible:      "दृश्यमान",
    deltaRemoved:      "हटाइएको",
    searchPlaceholder: "🔍 प्रतिक्रिया खोज्नुहोस्…",
    filterAllStatus:   "सबै स्थिति",
    statusPending:     "विचाराधीन",
    statusApproved:    "स्वीकृत",
    statusRejected:    "अस्वीकृत",
    filterAllCat:      "सबै वर्गहरू",
    refresh:           "↻ ताजा गर्नुहोस्",
    colMessage:        "सन्देश",
    colCategory:       "वर्ग",
    colStatus:         "स्थिति",
    colDate:           "मिति",
    colActions:        "कार्य",
    loading:           "प्रतिक्रिया लोड हुँदैछ…",
    empty:             "कुनै प्रतिक्रिया भेटिएन।",
    view:              "👁 हेर्नुहोस्",
    approve:           "✓ स्वीकृत",
    reject:            "✕ अस्वीकृत",
    savingDots:        "…",
    rejectConfirm:     "के तपाईं यो प्रतिक्रिया अस्वीकृत गर्न (स्थायी रूपमा मेटाउन) निश्चित हुनुहुन्छ?",
    modalTitle:        "💬 प्रतिक्रिया विवरण",
    lblMessage:        "सन्देश",
    lblCategory:       "वर्ग",
    lblStatus:         "स्थिति",
    lblSubmittedOn:    "पेश गरिएको",
    lblSubmitterInfo:  "पेश गर्नेको जानकारी",
    approveBtn:        "✓ स्वीकृत गर्नुहोस्",
    rejectBtn:         "✕ अस्वीकृत गर्नुहोस्",
    close:             "बन्द गर्नुहोस्",
    errLoad:           "प्रतिक्रिया लोड गर्न असफल भयो।",
    errStatus:         "स्थिति अपडेट गर्न असफल भयो।",
    errReject:         "अस्वीकृत गर्न असफल भयो।",
    locale:            "ne-NP",
  },
  en: {
    statTotal:         "Total",
    statPending:       "Pending",
    statApproved:      "Approved",
    statRejected:      "Rejected",
    deltaSoFar:        "So far",
    deltaReview:       "Review",
    deltaVisible:      "Visible",
    deltaRemoved:      "Removed",
    searchPlaceholder: "🔍 Search feedback…",
    filterAllStatus:   "All Statuses",
    statusPending:     "Pending",
    statusApproved:    "Approved",
    statusRejected:    "Rejected",
    filterAllCat:      "All Categories",
    refresh:           "↻ Refresh",
    colMessage:        "Message",
    colCategory:       "Category",
    colStatus:         "Status",
    colDate:           "Date",
    colActions:        "Actions",
    loading:           "Loading feedback…",
    empty:             "No feedback found.",
    view:              "👁 View",
    approve:           "✓ Approve",
    reject:            "✕ Reject",
    savingDots:        "…",
    rejectConfirm:     "Are you sure you want to reject this feedback (permanently delete it)?",
    modalTitle:        "💬 Feedback Details",
    lblMessage:        "Message",
    lblCategory:       "Category",
    lblStatus:         "Status",
    lblSubmittedOn:    "Submitted On",
    lblSubmitterInfo:  "Submitter Info",
    approveBtn:        "✓ Approve",
    rejectBtn:         "✕ Reject",
    close:             "Close",
    errLoad:           "Failed to load feedback.",
    errStatus:         "Failed to update status.",
    errReject:         "Failed to reject.",
    locale:            "en-US",
  },
};

function FeedbackPage() {
  const { language } = useLanguage();
  const t = CONTENT[language] || CONTENT.en;
  const isMobile = useIsMobile();

  const [feedbacks, setFeedbacks]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [search, setSearch]             = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCat, setFilterCat]       = useState("all");
  const [saving, setSaving]             = useState(null);
  const [viewModal, setViewModal]       = useState(null);

  useEffect(() => { fetchFeedback(); }, []);

  async function fetchFeedback() {
    setLoading(true); setError("");
    try {
      const res  = await api.get("/feedback/all");
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      const active   = data.filter(x => x.status !== "rejected");
      const rejected = data.filter(x => x.status === "rejected");
      setFeedbacks(active);
      // Cleanup: permanently remove any already-rejected records from the database
      rejected.forEach(x => { api.delete(`/feedback/${x._id}`).catch(() => {}); });
    } catch (e) {
      setError(e.response?.data?.message || t.errLoad);
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(id, status) {
    setSaving(id);
    try {
      const res     = await api.put(`/feedback/${id}`, { status });
      const updated = res.data?.data ?? res.data;
      setFeedbacks(f => f.map(x => x._id === id ? { ...x, status: updated.status } : x));
    } catch (e) {
      alert(e.response?.data?.message || t.errStatus);
    } finally {
      setSaving(null);
    }
  }

  async function handleReject(id) {
    if (!window.confirm(t.rejectConfirm)) return;
    setSaving(id);
    try {
      await api.delete(`/feedback/${id}`);
      setFeedbacks(f => f.filter(x => x._id !== id));
    } catch (e) {
      alert(e.response?.data?.message || t.errReject);
    } finally {
      setSaving(null);
    }
  }

  const categories = ["all", ...Array.from(new Set(feedbacks.map(f => f.category).filter(Boolean)))];

  const filtered = feedbacks.filter(f => {
    const q = search.toLowerCase();
    const matchSearch = f.message?.toLowerCase().includes(q) || f.category?.toLowerCase().includes(q);
    const matchStatus = filterStatus === "all" || f.status === filterStatus;
    const matchCat    = filterCat    === "all" || f.category === filterCat;
    return matchSearch && matchStatus && matchCat;
  });

  const stats = [
    { label: t.statTotal,    value: feedbacks.length,                                      icon: "💬", delta: t.deltaSoFar  },
    { label: t.statPending,  value: feedbacks.filter(f => f.status === "pending").length,  icon: "⏳", delta: t.deltaReview  },
    { label: t.statApproved, value: feedbacks.filter(f => f.status === "approved").length, icon: "✅", delta: t.deltaVisible },
    { label: t.statRejected, value: feedbacks.filter(f => f.status === "rejected").length, icon: "❌", delta: t.deltaRemoved },
  ];

  function fmt(dateStr) {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString(t.locale, { day: "numeric", month: "short", year: "numeric" });
  }

  const FB_COLS = "2fr 180px 110px 100px 260px";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Stat cards: 4 on desktop, 2 on mobile */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 10 }}>
        {stats.map(s => (
          <div key={s.label} style={S.statCard}>
            <div style={S.statIcon}>{s.icon}</div>
            <div>
              <div style={S.statVal}>{s.value}</div>
              <div style={S.statLabel}>{s.label}</div>
              <div style={S.statDelta}>{s.delta}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...S.card, padding: "12px 16px" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input placeholder={t.searchPlaceholder} value={search} onChange={e => setSearch(e.target.value)} style={{ ...INP, flex: "1 1 200px", width: "auto", minWidth: 0, maxWidth: isMobile ? "100%" : 260 }} />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...INP, width: "auto", minWidth: 120 }}>
            <option value="all">{t.filterAllStatus}</option>
            <option value="pending">{t.statusPending}</option>
            <option value="approved">{t.statusApproved}</option>
            <option value="rejected">{t.statusRejected}</option>
          </select>
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...INP, width: "auto", minWidth: 140 }}>
            <option value="all">{t.filterAllCat}</option>
            {categories.filter(c => c !== "all").map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={fetchFeedback} style={{ marginLeft: isMobile ? 0 : "auto", padding: "7px 14px", borderRadius: 7, background: G3, border: `1px solid ${G}`, color: G, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{t.refresh}</button>
        </div>
      </div>

      {error && <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 16px", color: "#ef4444", fontSize: 13 }}>⚠ {error}</div>}

      <div style={{ ...S.tableWrap, ...S.tableScroll }}>
        <div style={{ minWidth: isMobile ? 700 : undefined }}>
          <div style={tblHead(FB_COLS, isMobile)}>
            <span>{t.colMessage}</span><span>{t.colCategory}</span><span>{t.colStatus}</span><span>{t.colDate}</span><span>{t.colActions}</span>
          </div>
          {loading && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.loading}</div>}
          {!loading && filtered.length === 0 && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.empty}</div>}
          {!loading && filtered.map((f, i) => (
            <TblRow key={f._id} cols={FB_COLS} isLast={i === filtered.length - 1}>
              <TblCell>
                <div style={{ minWidth: 0, width: "100%" }}>
                  <div style={{ fontWeight: 500, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12 }}>{f.message}</div>
                </div>
              </TblCell>
              <TblCell fit>
                <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 10, fontSize: 10, fontWeight: 600, background: O2, color: O, border: `1px solid ${O}`, whiteSpace: "normal", lineHeight: 1.4, wordBreak: "break-word", maxWidth: "100%" }}>{f.category || "—"}</span>
              </TblCell>
              <TblCell fit><StatusBadge status={f.status} language={language} /></TblCell>
              <TblCell fit>
                <span style={{ color: "#6b7280", fontSize: 11, whiteSpace: "nowrap" }}>{fmt(f.createdAt)}</span>
              </TblCell>
              <TblCell fit>
                <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                  <button onClick={() => setViewModal(f)}
                    style={{ background: O2, border: `1px solid ${O}`, color: O, borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                    {t.view}
                  </button>
                  {f.status !== "approved" && (
                    <button disabled={saving === f._id} onClick={() => updateStatus(f._id, "approved")}
                      style={{ background: G3, border: `1px solid ${G}`, color: G, borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600, opacity: saving === f._id ? 0.6 : 1, whiteSpace: "nowrap" }}>
                      {saving === f._id ? t.savingDots : t.approve}
                    </button>
                  )}
                  {f.status !== "rejected" && (
                    <button disabled={saving === f._id} onClick={() => handleReject(f._id)}
                      style={{ background: "rgba(239,68,68,0.08)", border: "1px solid #fca5a5", color: "#ef4444", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600, opacity: saving === f._id ? 0.6 : 1, whiteSpace: "nowrap" }}>
                      {saving === f._id ? t.savingDots : t.reject}
                    </button>
                  )}
                </div>
              </TblCell>
            </TblRow>
          ))}
        </div>
      </div>

   {viewModal && (
  <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setViewModal(null)}>
    <div style={{ background: "#fff", borderRadius: 14, width: 460, maxWidth: "95vw", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", animation: "slideUp 0.22s ease", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
      <div style={{ background: `linear-gradient(135deg, ${G} 0%, #0a5c2e 100%)`, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>{t.modalTitle}</span>
        <button onClick={() => setViewModal(null)} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 26, height: 26, borderRadius: "50%", cursor: "pointer", fontSize: 14 }}>×</button>
      </div>
      <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 14 }}>

        <div>
          <div style={LBL}>{t.lblMessage}</div>
          <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, background: "#f9fafb", borderRadius: 8, padding: "12px 14px" }}>{viewModal.message}</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr", gap: 12 }}>
          <div><div style={LBL}>{t.lblCategory}</div><div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>{viewModal.category || "—"}</div></div>
          <div><div style={LBL}>{t.lblStatus}</div><StatusBadge status={viewModal.status} language={language} /></div>
          <div style={isMobile ? { gridColumn: "1 / -1" } : undefined}><div style={LBL}>{t.lblSubmittedOn}</div><div style={{ fontSize: 13, color: "#6b7280" }}>{fmt(viewModal.createdAt)}</div></div>
        </div>

        {(viewModal.name || viewModal.email || viewModal.phone) && (
          <div style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 14px" }}>
            <div style={LBL}>{language === 'ne' ? 'सम्पर्क विवरण' : 'Contact Details'}</div>
            {viewModal.name && <div style={{ fontSize: 13, color: "#111827", fontWeight: 600 }}>{viewModal.name}</div>}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 2 }}>
              {viewModal.email && <div style={{ fontSize: 12, color: "#6b7280" }}>✉ {viewModal.email}</div>}
              {viewModal.phone && <div style={{ fontSize: 12, color: "#6b7280" }}>☎ {viewModal.phone}</div>}
            </div>
            {viewModal.subject && <div style={{ fontSize: 12, color: "#4b5563", marginTop: 4 }}><strong>विषय:</strong> {viewModal.subject}</div>}
          </div>
        )}

        {viewModal.submittedBy && (
          <div style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 14px" }}>
            <div style={LBL}>{t.lblSubmitterInfo}</div>
            <div style={{ fontSize: 13, color: "#111827", fontWeight: 600 }}>{viewModal.submittedBy.name}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{viewModal.submittedBy.email}</div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          {viewModal.status !== "approved" && (
            <button onClick={() => { updateStatus(viewModal._id, "approved"); setViewModal(null); }}
              style={{ flex: isMobile ? "1 1 100%" : 1, padding: "9px", borderRadius: 8, background: G, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {t.approveBtn}
            </button>
          )}
          {viewModal.status !== "rejected" && (
            <button onClick={() => { handleReject(viewModal._id); setViewModal(null); }}
              style={{ flex: isMobile ? "1 1 100%" : 1, padding: "9px", borderRadius: 8, background: "rgba(239,68,68,0.08)", color: "#ef4444", border: "1.5px solid #ef4444", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {t.rejectBtn}
            </button>
          )}
          <button onClick={() => setViewModal(null)}
            style={{ flex: isMobile ? "1 1 100%" : 1, padding: "9px", borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            {t.close}
          </button>
        </div>

      </div>
    </div>
  </div>
)}
      <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

export default FeedbackPage;
